import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSetCustomDomain } from "@/queries/organization-queries";
import { isValidDomain, normalizeDomain } from "@/utils/url-validation";
import { formatError } from "@/utils/errors";
import { TrackLocations } from "@/hooks/use-segment";
import { type FC, useState, useEffect } from "react";
import { CustomDomainProvider, type CustomDomain } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";

type SetupCustomDomainDialogProps = {
    organizationId: string;
    isOpen: boolean;
    onClose: () => void;
    isEditing?: boolean;
    existingDomain?: CustomDomain;
};

function normalizeAwsAccountId(accountId: string): string {
    return accountId.replace(/[-\s]/g, "");
}

function isValidAwsAccountId(accountId: string): boolean {
    if (!accountId || accountId.trim().length === 0) {
        return false;
    }
    const normalized = normalizeAwsAccountId(accountId);
    // Must be exactly 12 digits
    return /^\d{12}$/.test(normalized);
}

function isValidGcpProjectId(projectId: string): boolean {
    if (!projectId || projectId.trim().length === 0) {
        return false;
    }
    // GCP Project IDs: 6-30 characters, lowercase letters, digits, hyphens
    // Must start with a letter, cannot end with a hyphen
    return /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(projectId);
}

function validateDomain(value: string): string {
    if (!value.trim()) {
        return "Domain is required";
    }
    // Normalize domain before validation (removes protocol if present)
    const normalized = normalizeDomain(value);
    if (!isValidDomain(normalized)) {
        return "Please enter a valid domain (e.g., company.com)";
    }
    return "";
}

function validateCloudAccountId(value: string, provider: CustomDomainProvider): string {
    if (!value.trim()) {
        return provider === CustomDomainProvider.AWS
            ? "AWS Account ID is required"
            : "GCP Project ID is required";
    }
    if (provider === CustomDomainProvider.AWS) {
        if (!isValidAwsAccountId(value)) {
            return "AWS Account ID must be exactly 12 digits";
        }
    } else if (provider === CustomDomainProvider.GCP) {
        if (!isValidGcpProjectId(value)) {
            return "GCP Project ID must be 6-30 characters, lowercase letters, digits, and hyphens";
        }
    }
    return "";
}

export const SetupCustomDomainDialog: FC<SetupCustomDomainDialogProps> = ({
    organizationId,
    isOpen,
    onClose,
    isEditing = false,
    existingDomain,
}) => {
    const { toast } = useToast();
    const setCustomDomain = useSetCustomDomain();

    const [provider, setProvider] = useState<CustomDomainProvider>(CustomDomainProvider.AWS);
    const [domain, setDomain] = useState("");
    const [cloudAccountId, setCloudAccountId] = useState("");
    const [domainError, setDomainError] = useState("");
    const [cloudAccountIdError, setCloudAccountIdError] = useState("");
    const [domainTouched, setDomainTouched] = useState(false);
    const [cloudAccountIdTouched, setCloudAccountIdTouched] = useState(false);

    // Pre-fill form when editing
    useEffect(() => {
        if (isEditing && existingDomain) {
            setDomain(existingDomain.domainName || "");
            // Use cloudAccountId if available, fall back to awsAccountId for backward compatibility
            setCloudAccountId(existingDomain.cloudAccountId || existingDomain.awsAccountId || "");
            setProvider(existingDomain.provider || CustomDomainProvider.AWS);
        } else {
            setDomain("");
            setCloudAccountId("");
            setProvider(CustomDomainProvider.AWS);
        }
        setDomainError("");
        setCloudAccountIdError("");
        setDomainTouched(false);
        setCloudAccountIdTouched(false);
    }, [isEditing, existingDomain, isOpen]);

    const handleProviderChange = (value: string) => {
        const newProvider = value === "gcp" ? CustomDomainProvider.GCP : CustomDomainProvider.AWS;
        setProvider(newProvider);
        // Re-validate cloud account ID with new provider rules
        if (cloudAccountIdTouched) {
            setCloudAccountIdError(validateCloudAccountId(cloudAccountId, newProvider));
        }
    };

    const handleDomainBlur = () => {
        setDomainTouched(true);
        setDomainError(validateDomain(domain));
    };

    const handleCloudAccountIdBlur = () => {
        setCloudAccountIdTouched(true);
        setCloudAccountIdError(validateCloudAccountId(cloudAccountId, provider));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate all fields
        const domainErr = validateDomain(domain);
        const cloudAccountIdErr = validateCloudAccountId(cloudAccountId, provider);

        setDomainError(domainErr);
        setCloudAccountIdError(cloudAccountIdErr);
        setDomainTouched(true);
        setCloudAccountIdTouched(true);

        if (domainErr || cloudAccountIdErr) {
            return;
        }

        // Normalize the cloud account ID based on provider
        const normalizedCloudAccountId =
            provider === CustomDomainProvider.AWS
                ? normalizeAwsAccountId(cloudAccountId)
                : cloudAccountId.trim().toLowerCase();

        try {
            await setCustomDomain.mutateAsync({
                organizationId,
                domainName: normalizeDomain(domain),
                provider,
                cloudAccountId: normalizedCloudAccountId,
            });

            toast({
                title: isEditing ? "Custom domain updated successfully" : "Custom domain configured successfully",
            });

            onClose();
        } catch (error) {
            toast({
                title: isEditing ? "Failed to update custom domain" : "Failed to configure custom domain",
                description: formatError(error),
            });
        }
    };

    const isAws = provider === CustomDomainProvider.AWS;
    const accountIdLabel = isAws ? "AWS Account ID" : "GCP Project ID";
    const accountIdPlaceholder = isAws ? "123456789012" : "my-project-id";
    const accountIdHelpText = isAws
        ? "Enter your 12-digit AWS account ID."
        : "Enter your GCP project ID (6-30 characters, lowercase).";

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent data-track-location={TrackLocations.SetupCustomDomainModal}>
                <DialogHeader>
                    <DialogTitle>{isEditing ? "Edit custom domain" : "Set up custom domain"}</DialogTitle>
                    <DialogDescription>
                        Register your custom domain to access Ona through a branded URL. See the{" "}
                        <ExternalLink href="https://ona.com/docs/ona/custom-domain" className="text-sm">
                            infrastructure setup guide
                        </ExternalLink>{" "}
                        to complete configuration.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <Label>Cloud Provider</Label>
                            <RadioGroup
                                value={isAws ? "aws" : "gcp"}
                                onValueChange={handleProviderChange}
                                className="flex flex-row gap-6"
                            >
                                <label className="flex flex-row items-center gap-2 cursor-pointer">
                                    <RadioGroupItem value="aws" />
                                    <span className="text-sm">AWS</span>
                                </label>
                                <label className="flex flex-row items-center gap-2 cursor-pointer">
                                    <RadioGroupItem value="gcp" />
                                    <span className="text-sm">GCP</span>
                                </label>
                            </RadioGroup>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label htmlFor="cloud-account-id">{accountIdLabel}</Label>
                            <Input
                                id="cloud-account-id"
                                type="text"
                                placeholder={accountIdPlaceholder}
                                value={cloudAccountId}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setCloudAccountId(e.target.value);
                                    if (cloudAccountIdTouched) {
                                        setCloudAccountIdError(validateCloudAccountId(e.target.value, provider));
                                    }
                                }}
                                onBlur={handleCloudAccountIdBlur}
                                aria-required="true"
                                aria-invalid={!!cloudAccountIdError}
                                aria-describedby={cloudAccountIdError ? "cloud-account-id-error" : "cloud-account-id-help"}
                            />
                            {cloudAccountIdError && cloudAccountIdTouched ? (
                                <p id="cloud-account-id-error" className="text-sm text-content-destructive" role="alert">
                                    {cloudAccountIdError}
                                </p>
                            ) : (
                                <p id="cloud-account-id-help" className="text-sm text-content-secondary">
                                    {accountIdHelpText}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label htmlFor="domain">Domain name</Label>
                            <Input
                                id="domain"
                                type="text"
                                placeholder="ona.example.com"
                                value={domain}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setDomain(e.target.value);
                                    if (domainTouched) {
                                        setDomainError(validateDomain(e.target.value));
                                    }
                                }}
                                onBlur={handleDomainBlur}
                                aria-required="true"
                                aria-invalid={!!domainError}
                                aria-describedby={domainError ? "domain-error" : "domain-help"}
                            />
                            {domainError && domainTouched ? (
                                <p id="domain-error" className="text-sm text-content-destructive" role="alert">
                                    {domainError}
                                </p>
                            ) : (
                                <>
                                    <p id="domain-help" className="text-sm text-content-secondary">
                                        Enter domain without https:// (e.g., company.com or login.company.com)
                                    </p>
                                    {domain && normalizeDomain(domain) && (
                                        <p className="text-xs text-content-secondary">
                                            Will be accessible at: <span className="font-medium">https://{normalizeDomain(domain)}</span>
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </form>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={setCustomDomain.isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSubmit}
                        loading={setCustomDomain.isPending}
                        data-tracking-id="submit-custom-domain"
                    >
                        {isEditing ? "Update" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
